import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Pill, SignerSelect } from './components';
import { ReactComponent as PenSignature } from '../../../../../../../shared/icons/signature-with-pen.svg';
import { ReactComponent as Initials } from '../../../../../../../shared/icons/initials.svg';
import { ReactComponent as Calendar } from '../../../../../../../shared/icons/calendar-2.svg';
import { ReactComponent as Text } from '../../../../../../../shared/icons/text.svg';
import { FieldToAdd } from '../../../../../../../pages/signing/prepareToSigningPdfJs';
import Tooltip, { ConfirmationTooltip, ErrorTooltip } from '../../../../../../tooltip';
import styles from './styles.module.scss';
import { AddSignerForm } from '../addSignerForm';

export type Signer = {
  id: string;
  email: string;
  order: number;
};

interface InsertFieldsProps {
  addField?: ({ ...keys }: FieldToAdd) => void;
  signers: Signer[];
  className?: string;
  showSignerAddedTooltip: boolean;
  showDuplicatedErrorTooltip: boolean;
  isCreatingSigner: boolean;
  onAddSignerSubmit: (email: string) => void;
  viewer?: HTMLDivElement | null;
}

export enum AnnotationTypes {
  SIGNATURE = 'signature',
  TEXT = 'text',
  DATE = 'date_signed',
  INITIALS = 'initials',
}

const InsertFields = ({
  addField,
  signers,
  className,
  onAddSignerSubmit,
  isCreatingSigner,
  showSignerAddedTooltip = false,
  showDuplicatedErrorTooltip = false,
  viewer,
}: InsertFieldsProps) => {
  const [selectedSignerId, setSelectedSignerId] = useState(signers[0]?.id);
  const previousSignersLength = useRef(signers.length);

  // Auto-select newly added signer
  useEffect(() => {
    if (signers.length > previousSignersLength.current) {
      // A new signer was added - select the last one (most recently added)
      const newestSigner = signers[signers.length - 1];
      if (newestSigner) {
        setSelectedSignerId(newestSigner.id);
      }
    }
    previousSignersLength.current = signers.length;
  }, [signers]);

  const dragStart = (e: any) => {
    e.target.style.opacity = 0.5;
    const copy = e.target.cloneNode(true);
    copy.id = 'form-build-drag-image-copy';
    copy.style.width = '250px';
    document.body.appendChild(copy);
    e.dataTransfer.setDragImage(copy, 125, 25);
    e.dataTransfer.setData('text', '');
  };

  const dragEnd = (e: any, type: AnnotationTypes) => {
    if (addField) {
      const selectedSigner = signers.find((s: any) => s.id === selectedSignerId) as Signer;

      // Start with page-relative coordinates (works with existing helpers.ts)
      let x = e.pageX;
      let y = e.pageY;

      // @ts-ignore
      addField({
        type,
        name: type,
        value: selectedSigner.email,
        signerId: selectedSigner.id,
        signerOrder: selectedSigner.order,
        dropPoint: {
          x,
          y,
        },
      });
    }
    e.target.style.opacity = 1;
    const dragImage = document.getElementById('form-build-drag-image-copy');
    if (dragImage) {
      document.body.removeChild(dragImage);
    }
    e.preventDefault();
  };

  const selectedSigner = signers.find((s) => s.id === selectedSignerId);

  return (
    <div className={classNames(styles.wrapper, className)}>
      <h1 className={styles.title}>
        Add/Insert Signers
        <Tooltip placement="right" testId="insert-signers-tooltip">
          Select a signer and drag fields for signature, initials, date signed and/or text in to document.
        </Tooltip>
      </h1>
      <AddSignerForm onSubmit={(email: string) => onAddSignerSubmit(email)} isCreatingSigner={isCreatingSigner} />
      <div className={styles['fields-wrapper']}>
        <div className={styles['fields-wrapper__signersDropdownContainer']}>
          {/*// @ts-ignore*/}
          <SignerSelect signers={signers} onSelect={setSelectedSignerId} selectedSigner={selectedSigner} />
          <ConfirmationTooltip show={showSignerAddedTooltip} testId="lease-document-tooltip" placement="right" icon="">
            Signer added!
          </ConfirmationTooltip>
          <ErrorTooltip show={showDuplicatedErrorTooltip} testId="lease-document-tooltip" placement="right" icon="">
            This signer already exists
          </ErrorTooltip>
        </div>

        <div className={styles['pills-container']}>
          <Pill
            title="Signature"
            icon={PenSignature}
            onDragStart={(e) => dragStart(e)}
            onDragEnd={(e) => dragEnd(e, AnnotationTypes.SIGNATURE)}
            disabled={!selectedSigner}
          />
          <Pill
            title="Initials"
            icon={Initials}
            onDragStart={(e) => dragStart(e)}
            onDragEnd={(e) => dragEnd(e, AnnotationTypes.INITIALS)}
            disabled={!selectedSigner}
          />
          <Pill
            title="Date Signed"
            icon={Calendar}
            onDragStart={(e) => dragStart(e)}
            onDragEnd={(e) => dragEnd(e, AnnotationTypes.DATE)}
            disabled={!selectedSigner}
          />
          <Pill
            title="Text"
            icon={Text}
            onDragStart={(e) => dragStart(e)}
            onDragEnd={(e) => dragEnd(e, AnnotationTypes.TEXT)}
            disabled={!selectedSigner}
          />
        </div>
      </div>
    </div>
  );
};

export default InsertFields;
